/**
 * Searchspring Collection Provider
 * Exports a `provider` object implementing the collection provider contract
 * ({ init, fetchProducts, reorderGrid, onRendered }) for Searchspring merchandising.
 * @module searchspring-collection
 */

import { SITE_ID, API_BASE, RESULTS_PER_PAGE, getUserId, getSessionId, PAGE_LOAD_ID, trackRender, createImpressionObserver, attachClickTracking, buildSSResultMap } from '@stardust/searchspring-base';
import { initSearchPage } from '@stardust/searchspring-search-page';
import { renderFacets, renderMobileFilterTrigger, SORT_OPTIONS, getFacetValueKey, getFacetValueLabel } from '@stardust/searchspring-facets';
import { buildFieldMap, buildDualFilterURL, buildSSOnlyFilterURL, getSDOnlyFilterDefs } from '@stardust/searchspring-filter-mapping';
import { reorderProductCards, renderSSOnlyGrid, waitForAboveFoldImagesLoaded, insertInlineBanners } from '@stardust/searchspring-collection-renderer';
import { buildProductCardFromSS } from '@stardust/ss-product-card-builder';
import { initProductCards } from '@stardust/product-card';
import { observeProductCards } from '@stardust/swatch-hydrator';
import * as S from '@stardust/searchspring-collection-state';

let ssOnlyRequestSeq = 0;
let initialized = false;

/** Tracks the active AbortController for each load-more button so re-hydration can cleanly tear down prior listeners. */
const loadMoreAborters = new WeakMap();

/**
 * True when SearchSpring SSR is enabled for this render — whether the metaobject
 * cache was hit (real product cards from ssr-collection.liquid) or missed (skeleton
 * grid filled by a client-side fetch). Either way there's no S&D-rendered product
 * list to hybrid-reorder against, so syncAndApply() must force ss-only mode.
 */
let ssrMode = false;

/** Snapshot of the SSR grid innerHTML for restoration on clear-all. */
let ssrGridSnapshot = '';

/** Snapshot of the SSR filter section innerHTML for restoration on clear-all. */
let ssrFiltersSnapshot = '';

/** Snapshot of the SSR result count text for restoration on clear-all. */
let ssrCountSnapshot = '';

/** Fetch products from Searchspring API for a collection or search query. */
export async function fetchSearchspringProducts(collectionHandle = null, timeoutMs = 800, { query, page = 1, resultsPerPage = RESULTS_PER_PAGE } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (!query) query = new URLSearchParams(window.location.search).get('q') || undefined;
  const params = new URLSearchParams({
    siteId: SITE_ID, resultsFormat: 'json', resultsPerPage,
    page, beacon: true, userId: getUserId(), sessionId: getSessionId(), pageLoadId: PAGE_LOAD_ID,
    domain: window.location.href,
  });
  if (!collectionHandle) collectionHandle = document.querySelector('.collection-grid[data-collection-handle]')?.dataset?.collectionHandle;
  if (collectionHandle) params.set('bgfilter.collection_handle', collectionHandle);
  if (query) params.set('rq', query);
  for (const [field, values] of Object.entries(S.activeFilters)) {
    for (const value of values) {
      if (value.includes(':')) { const [l, h] = value.split(':'); params.append(`filter.${field}.low`, l); params.append(`filter.${field}.high`, h); }
      else params.append(`filter.${field}`, value);
    }
  }
  if (S.activeSort.field) params.set(`sort.${S.activeSort.field}`, S.activeSort.direction || 'asc');
  try {
    const r = await fetch(`${API_BASE}/search/category.json?${params}`, { signal: controller.signal });
    clearTimeout(timer);
    return r.ok ? await r.json() : null;
  } catch { clearTimeout(timer); return null; }
}

function setupTracking(data, gridElement, { module = 'category' } = {}) {
  const responseId = data.pagination?.responseId || data.responseId;
  if (!responseId || !gridElement) return;
  const ssResultMap = buildSSResultMap(data.results);
  trackRender(module, responseId);
  createImpressionObserver(module, responseId, gridElement, { ssResultMap });
  attachClickTracking(module, responseId, gridElement, { ssResultMap });
}

// ─── SSR Popularity Tracking ──────────────────────────────────────────────────

/**
 * Send a /track request to the SSR worker for the current URL.
 * Reads the endpoint and shop domain from the tracking embed's data attributes
 * on the [data-ssr-collection] container. No-ops silently if attributes missing.
 */
function trackSSRPageView() {
  const ssrEl = document.querySelector('[data-ssr-collection]');
  if (!ssrEl) return;
  const trackUrl = ssrEl.dataset.ssrTrackUrl;
  const shopDomain = ssrEl.dataset.ssrShopDomain;
  if (!trackUrl || !shopDomain) return;
  const payload = JSON.stringify({ url: window.location.href, shop: shopDomain });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(trackUrl, payload);
    } else {
      fetch(trackUrl, { method: 'POST', body: payload, keepalive: true }).catch(() => {});
    }
  } catch { /* tracking is best-effort */ }
}

// ─── Personalization Re-sort ──────────────────────────────────────────────────

/**
 * On SSR page 1 default sort, do a background fetch with user tracking IDs
 * (userId, sessionId, pageLoadId) so SS returns personalized results.
 * Silently reorder the grid if the personalized order differs from the cached SSR.
 */
async function personalizeSSRGrid(grid) {
  const data = await fetchSearchspringProducts(grid.dataset?.collectionHandle, 5000);
  if (!data?.results?.length) return;

  const ssOrder = data.results.map(r => r.handle);
  const cards = Array.from(grid.querySelectorAll(':scope > [data-product-handle]'));
  const currentOrder = cards.map(c => c.dataset.productHandle);

  // Check if order differs
  const orderChanged = ssOrder.length !== currentOrder.length ||
    ssOrder.some((h, i) => currentOrder[i] !== h);
  if (!orderChanged) return;

  // Remove inline banners before reordering so they don't get stranded
  grid.querySelectorAll(':scope > .searchspring-inline-banner').forEach(el => el.remove());

  // Reorder existing cards to match personalized SS order
  const cardMap = new Map(cards.map(c => [c.dataset.productHandle, c]));
  const fragment = document.createDocumentFragment();

  for (const handle of ssOrder) {
    const card = cardMap.get(handle);
    if (card) {
      fragment.appendChild(card);
      cardMap.delete(handle);
    }
  }
  // Append remaining cards not in SS results at the end
  for (const card of cardMap.values()) {
    fragment.appendChild(card);
  }

  grid.appendChild(fragment);

  const hasActiveFilters = Object.keys(S.activeFilters || {}).length > 0
                        || Object.keys(S.sdOnlyActiveFilters || {}).length > 0;
  insertInlineBanners(data, grid, { hasFilters: hasActiveFilters });

  // Update snapshot so clear-all restores to personalized order
  ssrGridSnapshot = grid.innerHTML;

  // Set up tracking for personalized results
  setupTracking(data, grid, { module: 'category' });
}

// ─── Load-More Deduplication ──────────────────────────────────────────────────

/**
 * Appends product cards for `results`, skipping any handle already rendered
 * in `gridEl`. Page 1 is server-rendered from the SSR cache, but every
 * subsequent "load more" page is a live SearchSpring fetch with a different
 * request context (real userId/sessionId vs. the cache's anonymous fetch) —
 * personalization or catalog drift between the two can shift product
 * ranking, which would otherwise re-show a product already on the page.
 * Checking by handle makes correctness independent of whether SS's ranking
 * happens to line up, rather than assuming it always will.
 * @returns {number} how many cards were actually appended (excludes skipped duplicates)
 */
function appendUniqueProductCards(gridEl, results) {
  const seenHandles = new Set(
    Array.from(gridEl.querySelectorAll(':scope > [data-product-handle]')).map(c => c.dataset.productHandle)
  );
  const fragment = document.createDocumentFragment();
  const linkWithin = gridEl.dataset?.collectionHandle || '';
  let appended = 0;
  for (const result of results) {
    if (!result?.handle || seenHandles.has(result.handle)) continue;
    fragment.appendChild(buildProductCardFromSS(result, { facetSelectionOrder: S.facetSelectionOrder, activeSort: S.activeSort, linkWithin }));
    seenHandles.add(result.handle);
    appended++;
  }
  gridEl.appendChild(fragment);
  return appended;
}

// ─── Filter/Sort Handlers ─────────────────────────────────────────────────────

function handleSDOnlyFacetChange(field, value, active) {
  if (field === '__clear_all__') {
    S.setSdOnlyActiveFilters({}); S.setActiveFilters({}); S.setActiveSort({});
    S.setFacetSelectionOrder([]); S.setRenderMode('hybrid'); syncAndApply(); return;
  }
  const cur = { ...S.sdOnlyActiveFilters };
  if (active) { if (!cur[field]) cur[field] = []; if (!cur[field].includes(value)) cur[field].push(value); }
  else if (cur[field]) { cur[field] = cur[field].filter(v => v !== value); if (!cur[field].length) delete cur[field]; }
  S.setSdOnlyActiveFilters(cur); syncAndApply();
}

async function handleFacetChange(field, value, active) {
  if (field === '__clear_all__') {
    S.setActiveFilters({}); S.setActiveSort({}); S.setSdOnlyActiveFilters({});
    S.setFacetSelectionOrder([]); syncAndApply(); return;
  }
  if (field === '__apply_batch__' && typeof value === 'object') {
    const ss = {}, sd = {}, sdp = new Set(S.sdOnlyFacets.map(d => d.paramName));
    for (const [f, v] of Object.entries(value)) (sdp.has(f) ? sd : ss)[f] = v;
    S.setActiveFilters(ss); S.setSdOnlyActiveFilters(sd);
    const ord = []; for (const [f, vs] of Object.entries(ss)) for (const v of vs) ord.push({ field: f, value: v });
    S.setFacetSelectionOrder(ord); syncAndApply(); return;
  }
  if (field !== undefined && value !== undefined) {
    const f = { ...S.activeFilters }; let ord = [...S.facetSelectionOrder];
    if (active) { if (!f[field]) f[field] = []; if (!f[field].includes(value)) f[field].push(value); ord.push({ field, value }); }
    else { if (f[field]) { f[field] = f[field].filter(v => v !== value); if (!f[field].length) delete f[field]; } ord = ord.filter(e => !(e.field === field && e.value === value)); }
    S.setActiveFilters(f); S.setFacetSelectionOrder(ord);
  }
  syncAndApply();
}

function handleSortChange(sortField, sortDir) {
  S.setActiveSort(sortField ? { field: sortField, direction: sortDir } : {}); syncAndApply();
}

function syncAndApply({ replaceState = false } = {}) {
  // In SSR mode, always use ss-only rendering — fieldMap and currentFacets
  // are not populated from S&D forms, so determineRenderMode would incorrectly
  // return 'hybrid' and the grid would never update.
  if (ssrMode) {
    S.setRenderMode('ss-only');
  } else {
    S.setRenderMode(S.determineRenderMode());
  }

  const m = replaceState ? 'replaceState' : 'pushState';
  if (S.renderMode === 'hybrid') {
    const url = buildDualFilterURL(S.activeFilters, S.currentFacets, S.fieldMap, window.location.href);
    for (const [p, vs] of Object.entries(S.sdOnlyActiveFilters)) for (const v of vs) url.searchParams.append(p, v);
    for (const k of [...url.searchParams.keys()]) if (k.startsWith('sort.') || k === 'sort_by') url.searchParams.delete(k);
    if (S.activeSort.field) {
      url.searchParams.set(`sort.${S.activeSort.field}`, S.activeSort.direction || 'asc');
      const mt = SORT_OPTIONS.find(o => o.field === S.activeSort.field && o.direction === S.activeSort.direction);
      if (mt?.sdSortBy) url.searchParams.set('sort_by', mt.sdSortBy);
    }
    url.searchParams.delete('page');
    history[m](null, '', url);
    // Hybrid mode: collection.js morphs this section from the server for the new
    // URL, so the server-rendered result count is already correct — don't recount
    // the DOM here (that runs before the morph lands and reports the old grid).
    document.dispatchEvent(new CustomEvent('ss:filters:applied', { detail: { url: url.toString() } }));
  } else {
    const url = buildSSOnlyFilterURL(S.activeFilters, window.location.href);
    if (S.activeSort.field) url.searchParams.set(`sort.${S.activeSort.field}`, S.activeSort.direction || 'asc');
    url.searchParams.delete('page');
    history[m](null, '', url);

    // In SSR mode, if clearing all filters/sort back to default state,
    // restore the original SSR-rendered content instead of re-fetching.
    const isDefaultState = Object.keys(S.activeFilters).length === 0 && !S.activeSort.field;
    if (ssrMode && isDefaultState && ssrGridSnapshot) {
      restoreSSRSnapshot();
    } else {
      doSSOnlyRender();
    }
  }

  // Fire SSR popularity tracking for the new URL state
  if (ssrMode) trackSSRPageView();
}

async function doSSOnlyRender() {
  const grid = document.querySelector('.collection-grid');
  if (!grid) return;
  grid.classList.add('searchspring-loading');
  const seq = ++ssOnlyRequestSeq;
  try {
    const data = await fetchSearchspringProducts();
    if (seq !== ssOnlyRequestSeq) return; // superseded — newer call owns the loading state
    if (!data) return;
    S.setCurrentFacets(data.facets);
    renderSSOnlyGrid(data, grid, { facetSelectionOrder: S.facetSelectionOrder });

    document.dispatchEvent(new CustomEvent('ssr:grid-replaced', { detail: { data } }));
    const fc = document.getElementById('collection-filters-section');
    if (fc && data.facets) renderFacets(data.facets, fc, { activeFilters: S.activeFilters, activeSort: S.activeSort, onFacetChange: handleFacetChange, onSortChange: handleSortChange });
    setupTracking(data, grid, { module: 'category' });
  } finally {
    // Only clear loading state if this is still the latest request
    if (seq === ssOnlyRequestSeq) {
      grid.classList.remove('searchspring-loading');
    }
  }
}

/**
 * Restore the original SSR-rendered grid and filter content.
 * Called when all filters/sort are cleared in SSR mode.
 *
 * Note: Event delegation listeners on #collection-filters-section survive
 * innerHTML replacement because they're on the container, not the children.
 * The load-more button listener is set up once by hydrateSSRFilters and
 * lives outside the grid, so it also survives.
 */
function restoreSSRSnapshot() {
  const grid = document.querySelector('.collection-grid');
  if (grid && ssrGridSnapshot) {
    grid.innerHTML = ssrGridSnapshot;
    initProductCards();
    observeProductCards(grid);
    grid.classList.remove('searchspring-loading');
  }
  const fc = document.getElementById('collection-filters-section');
  if (fc && ssrFiltersSnapshot) {
    // Preserve the live drawer element — same pattern as renderFacets() — so the
    // MobileFiltersDrawer JS instance's element reference stays valid after the replace.
    const liveDrawer = fc.querySelector('[data-mobile-filters-drawer]');
    fc.innerHTML = ssrFiltersSnapshot;
    if (liveDrawer) {
      // Update drawer content to match the snapshot (no active-filter tags)
      const snapDrawer = fc.querySelector('[data-mobile-filters-drawer]');
      const liveContent = liveDrawer.querySelector('[data-mobile-filters-content]');
      const snapContent = snapDrawer?.querySelector('[data-mobile-filters-content]');
      if (liveContent && snapContent) liveContent.innerHTML = snapContent.innerHTML;
      // Swap snapshot's static element for the live instance
      if (snapDrawer) snapDrawer.replaceWith(liveDrawer);
      else fc.appendChild(liveDrawer);
    }
    // Close the drawer — it may have been open when the user hit clear-all.
    // BaseDrawer.close() guards against double-close (isOpen check), so this is safe.
    document.dispatchEvent(new CustomEvent('mobile-filters:close'));
    // Reset the sticky trigger button count — it lives outside #collection-filters-section
    // and isn't touched by the innerHTML restore above.
    renderMobileFilterTrigger({});
  }
  // Restore result count to original SSR value
  const countEl = document.querySelector('[data-ss-result-count]');
  if (countEl && ssrCountSnapshot) {
    countEl.textContent = ssrCountSnapshot;
  }
  // Show load-more wrapper if it exists (it's a sibling of the grid, not inside it)
  const wrapper = document.querySelector('[data-ssr-load-more-wrapper]');
  if (wrapper) wrapper.style.display = '';
  // Bump load-more generation, reset paging state, and re-arm the auto-observer
  // so the next "View More" starts at page 2 of the restored unfiltered grid.
  document.dispatchEvent(new CustomEvent('ssr:grid-replaced'));
}

function facetOpts() {
  const o = { activeFilters: S.activeFilters, activeSort: S.activeSort, onFacetChange: handleFacetChange, onSortChange: handleSortChange };
  if (S.renderMode === 'hybrid' && S.sdOnlyFacets.length > 0) {
    o.extraFacets = S.sdOnlyFacets.map(d => S.sdDefToFacet(d));
    o.onExtraFacetChange = handleSDOnlyFacetChange;
    o.extraActiveFilters = S.sdOnlyActiveFilters;
  }
  return o;
}

function rerenderFacetsAfterMorph(data) {
  if (!data?.facets) return;
  S.setCurrentFacets(data.facets);
  const f = document.getElementById('CollectionFiltersForm-desktop');
  if (f) { const r = buildFieldMap(f); S.setFieldMap(r.fieldMap); S.setSdFilterDefs(r.sdFilterDefs); S.setSdOnlyFacets(getSDOnlyFilterDefs(r.sdFilterDefs, data.facets)); }
  const fc = document.getElementById('collection-filters-section');
  if (fc) renderFacets(data.facets, fc, facetOpts());
}

// ─── SSR Filter/Sort Hydration ────────────────────────────────────────────────

/**
 * When SSR products rendered from cache but Liquid emitted no facet inputs
 * (e.g. | size vs .size on parsed JSON, or cache payload omitted facets),
 * fetch category.json once and render the same facet UI as the non-SSR path.
 * @param {HTMLElement} grid
 * @returns {Promise<void>}
 */
async function ensureSSRFacetMarkupFromApi(grid) {
  const fc = document.getElementById('collection-filters-section');
  if (!fc || fc.querySelector('[data-ssr-facet-field]')) return;

  const handle = grid.dataset?.collectionHandle || null;
  const data = await fetchSearchspringProducts(handle, 5000);
  if (!data?.facets?.length) return;

  S.setCurrentFacets(data.facets);
  renderFacets(data.facets, fc, facetOpts());
  normalizeSSRFacetMarkup(fc);
}

/**
 * Convert Searchspring-rendered facet markup into SSR-delegation-safe markup.
 * - Adds SSR data attributes consumed by hydrateSSRFilters delegated handlers.
 * - Removes direct listeners by cloning controls so innerHTML snapshot restores
 *   continue to work after replacement.
 * @param {HTMLElement} container
 */
function normalizeSSRFacetMarkup(container) {
  if (!container) return;

  // Facet checkboxes: add SSR delegated attributes and remove direct listeners.
  const facetDropdowns = container.querySelectorAll('[data-filter-dropdown]');
  facetDropdowns.forEach((dropdown) => {
    const summaryLabel = dropdown.querySelector('summary .collection-filter__label');
    const rawLabel = summaryLabel?.textContent?.trim() || '';
    const facetLabel = rawLabel.replace(/\s+\(\d+\)\s*$/, '');

    dropdown.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      const label = checkbox.closest('label');
      const labelTextEl = label?.querySelector('.flex-1');
      const valueText = labelTextEl?.textContent?.trim() || '';
      if (!valueText || !facetLabel) return;

      const facet = S.currentFacets.find((f) => f.label === facetLabel);
      if (!facet) return;
      const valueMatch = facet.values.find((v) => getFacetValueLabel(v) === valueText);
      if (!valueMatch) return;

      const cleanCheckbox = checkbox.cloneNode(true);
      cleanCheckbox.setAttribute('data-ssr-facet-field', facet.field);
      cleanCheckbox.setAttribute('data-ssr-facet-value', getFacetValueKey(valueMatch));
      checkbox.replaceWith(cleanCheckbox);
    });
  });

  // Sort radios: add SSR delegated attributes and remove direct listeners.
  container.querySelectorAll('input[type="radio"][name="ss_sort"]').forEach((radio, idx) => {
    const option = SORT_OPTIONS[idx];
    if (!option) return;
    const cleanRadio = radio.cloneNode(true);
    cleanRadio.setAttribute('data-ss-sort-field', option.field);
    cleanRadio.setAttribute('data-ss-sort-dir', option.direction);
    radio.replaceWith(cleanRadio);
  });

  // Desktop clear-all tag button (if present) should be SSR delegated too.
  container.querySelectorAll('.collection-filters__control--clear-all').forEach((btn) => {
    btn.setAttribute('data-ssr-clear-all', '');
  });
}

/**
 * Wire up server-rendered SSR facet checkboxes and sort radios to the SS
 * client-side rendering pipeline. On first interaction the grid transitions
 * from static SSR to live SS-only mode.
 * Also handles "View More Products" pagination.
 */
function hydrateSSRFilters(grid) {
  const container = document.getElementById('collection-filters-section');

  if (container) {
    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      container.querySelectorAll('[data-filter-dropdown]').forEach(d => {
        if (!d.contains(e.target)) d.removeAttribute('open');
      });
    });

    // Facet checkbox changes → SS filter
    container.addEventListener('change', (e) => {
      if (e.target.closest('[data-mobile-filters-drawer]')) return;

      const cb = e.target.closest('[data-ssr-facet-value]');
      if (cb) {
        const field = cb.dataset.ssrFacetField;
        const value = cb.dataset.ssrFacetValue;
        handleFacetChange(field, value, cb.checked);
        return;
      }

      // Sort radio changes → SS sort
      const radio = e.target.closest('[data-ss-sort-field]');
      if (radio) {
        const details = radio.closest('details');
        if (details) details.removeAttribute('open');
        handleSortChange(radio.dataset.ssSortField, radio.dataset.ssSortDir);
      }
    });

    // Clear-all button click (mobile SSR drawer)
    container.addEventListener('click', (e) => {
      const clearBtn = e.target.closest('[data-ssr-clear-all]');
      if (clearBtn) {
        handleFacetChange('__clear_all__', '', false);
        return;
      }

      // Apply button (mobile SSR drawer): close first so the overlay starts
      // fading immediately, then run one batched fetch for all staged selections.
      const applyBtn = e.target.closest('[data-ssr-apply]');
      if (applyBtn) {
        const drawer = applyBtn.closest('[data-mobile-filters-drawer]');
        if (!drawer) return;

        const batch = {};
        drawer.querySelectorAll('[data-ssr-facet-value]:checked').forEach((cb) => {
          const field = cb.dataset.ssrFacetField;
          (batch[field] ||= []).push(cb.dataset.ssrFacetValue);
        });

        // Sort is staged too; only override state if the user changed it.
        const sortRadio = drawer.querySelector('[data-ss-sort-field]:checked');
        if (sortRadio) {
          const { field, direction } = S.activeSort;
          const changed = field
            ? sortRadio.dataset.ssSortField !== field || sortRadio.dataset.ssSortDir !== direction
            : !sortRadio.defaultChecked;
          if (changed) {
            S.setActiveSort({ field: sortRadio.dataset.ssSortField, direction: sortRadio.dataset.ssSortDir });
          }
        }

        document.dispatchEvent(new CustomEvent('mobile-filters:close'));
        requestAnimationFrame(() => handleFacetChange('__apply_batch__', batch));
      }
    });

    // On open, sync the static drawer inputs to the current filter/sort state.
    // This discards unapplied selections (backdrop/ESC/X closes) and reflects
    // filters that are already active. Only static Liquid inputs are touched;
    // SS-built drawer content manages its own state.
    document.addEventListener('mobile-filters:open', () => {
      const drawer = container.querySelector('[data-mobile-filters-drawer]');
      if (!drawer) return;

      drawer.querySelectorAll('[data-ssr-facet-value]').forEach((cb) => {
        cb.checked = !!S.activeFilters[cb.dataset.ssrFacetField]?.includes(cb.dataset.ssrFacetValue);
      });

      const { field, direction } = S.activeSort;
      drawer.querySelectorAll('[data-ss-sort-field]').forEach((radio) => {
        radio.checked = field
          ? radio.dataset.ssSortField === field && radio.dataset.ssSortDir === direction
          : radio.defaultChecked;
      });
    });
  }

  // "View More Products" infinite load with URL state management
  const loadMoreBtn = document.querySelector('[data-ssr-load-more]');
  if (loadMoreBtn) {
    // Tear down any previous wiring (defensive — if hydrateSSRFilters ever runs twice,
    // the prior load-more listeners and ssr:grid-replaced handler must not stack).
    loadMoreAborters.get(loadMoreBtn)?.abort();
    const ac = new AbortController();
    loadMoreAborters.set(loadMoreBtn, ac);
    const { signal } = ac;

    // perPage is the clean, always-multiple-of-4 batch size used for every page
    // after page 1. shownCount is how many products page 1 actually rendered —
    // it can run a few products ahead of perPage when a merchandising banner
    // padded that row out (see ssr-collection.liquid). Banners never appear past
    // page 1, so reusing shownCount as the ongoing fetch size would hand later
    // pages a non-multiple-of-4 batch with nothing to round it out. Instead we
    // buffer: fetch whole SearchSpring pages of perPage at a time, filter out
    // anything already shown by product handle, and only deliver a batch to
    // the grid once enough genuinely-new items have accumulated.
    const ssrContainer = document.querySelector('[data-ssr-collection]');
    // The button's data-per-page is the primary source (set on both the cache-hit and
    // cache-miss markup), but grid/ssrContainer's data-ssr-per-page — the same merchant
    // products_per_page setting RESULTS_PER_PAGE reads for the very first fetch — is a
    // reliable fallback so this can't silently drift to the hardcoded 24 if a template
    // ever omits the button attribute again.
    const perPage = parseInt(loadMoreBtn.dataset.perPage, 10)
      || parseInt(grid.dataset.ssrPerPage, 10)
      || parseInt(ssrContainer?.dataset.ssrPerPage, 10)
      || 24;
    const shownCount = parseInt(ssrContainer?.dataset.ssrShownCount, 10) || perPage;
    const urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
    const ssrPage = parseInt(ssrContainer?.dataset.ssrCurrentPage, 10) || 1;
    let currentPage = ssrPage;
    let loading = false;
    const targetPage = Math.max(urlPage, ssrPage);

    // Starting page is just an efficiency guess to avoid re-fetching page 1's
    // content pointlessly — correctness doesn't depend on it being exact.
    let consumed = shownCount;
    let nextPageToFetch = Math.floor(shownCount / perPage) + 1;
    let carry = [];
    let noMoreResults = false;
    // True when the most recent fillCarryTo() call stopped because a fetch errored/timed
    // out, as opposed to SearchSpring genuinely running out of pages. Kept separate from
    // noMoreResults so a network hiccup doesn't get treated as "end of catalog" and
    // permanently hide the load-more button — see doLoadMore().
    let fetchFailed = false;

    const autoTrigger = document.querySelector('[data-ssr-load-more-auto]');
    let loadMoreGen = 0;
    // Preserve the merchant's configured button label (collection_view_more_label)
    // rather than hardcoding English when the button is re-shown after a load.
    const viewMoreLabel = (loadMoreBtn.textContent || '').trim() || 'View More Products';

    function currentShownHandles() {
      return new Set(
        Array.from(grid.querySelectorAll(':scope > [data-product-handle]')).map(c => c.dataset.productHandle)
      );
    }

    // Reflect the current page in the URL so a reload/share deep-links back to it.
    function setPageInUrl(page) {
      const url = new URL(window.location.href);
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      history.replaceState(null, '', url);
    }

    // Switch the control to the manual "View More" button (auto-load fires once).
    function showManualLoadMore() {
      const wrapper = loadMoreBtn.closest('[data-ssr-load-more-wrapper]');
      if (wrapper) wrapper.style.display = '';
      loadMoreBtn.textContent = viewMoreLabel;
      loadMoreBtn.disabled = false;
      loadMoreBtn.classList.remove('hidden');
      if (autoTrigger) autoTrigger.classList.add('hidden');
    }

    function hideLoadMore() {
      loadMoreBtn.closest('[data-ssr-load-more-wrapper]')?.style.setProperty('display', 'none');
    }

    /**
     * Fetch whole SearchSpring pages (fixed perPage size) until the carry
     * buffer holds at least `target` genuinely-new items, or SearchSpring
     * runs out. Filters every fetched item against what's already rendered
     * (or already queued) by handle rather than assuming a fixed rank offset
     * — SearchSpring's live fetch carries real userId/sessionId while the
     * cache-building fetch on the worker doesn't, so its ranking can drift
     * from the cached page 1 order. A fixed "skip N known-overlapping items"
     * assumption breaks silently when that drift adds extra overlap; fetching
     * more pages until we actually have `target` new items doesn't.
     */
    async function fillCarryTo(target, collectionHandle) {
      fetchFailed = false;
      const shown = currentShownHandles();
      const carryHandles = new Set(carry.map(r => r.handle));
      while (carry.length < target && !noMoreResults && !fetchFailed) {
        const data = await fetchSearchspringProducts(collectionHandle, 5000, {
          page: nextPageToFetch,
          resultsPerPage: perPage,
        });
        // fetchSearchspringProducts resolves null on a request error/timeout as well as
        // on a genuinely empty response — only the latter means "no more pages".
        if (data == null) { fetchFailed = true; break; }
        if (!data.results?.length) { noMoreResults = true; break; }
        for (const result of data.results) {
          if (result?.handle && !shown.has(result.handle) && !carryHandles.has(result.handle)) {
            carry.push(result);
            carryHandles.add(result.handle);
          }
        }
        nextPageToFetch += 1;

        // A short page (fewer than perPage) is NOT a reliable "last page" signal on its
        // own — confirmed live: SearchSpring can trim a MIDDLE page by a couple of items
        // (merchandising/exclusion rules applied per-request) while later pages still
        // have plenty of content (e.g. currentPage 2 of totalPages 4, 23 back instead of
        // 24). Trust the page/total-page count SS reports for THIS response first; only
        // fall back to "was this page short" when that metadata is entirely missing.
        const pg = data.pagination;
        let isLastPage;
        if (pg?.totalPages != null && pg?.currentPage != null) {
          isLastPage = pg.currentPage >= pg.totalPages;
        } else if (pg?.totalResults != null && pg?.end != null) {
          isLastPage = pg.end >= pg.totalResults;
        } else {
          isLastPage = data.results.length < perPage;
        }
        if (isLastPage) { noMoreResults = true; break; }
      }
    }

    /**
     * Catch up to the URL page state by filling the carry buffer to cover
     * pages ssrPage+1..targetPage, then appending them all at once. SSR
     * always renders page 1; if URL says ?page=3, we need 2 more
     * perPage-sized batches beyond what page 1 already rendered.
     */
    async function catchUpPages() {
      if (targetPage <= ssrPage) return;

      // Hide load-more button during catch-up
      const wrapper = loadMoreBtn?.closest('[data-ssr-load-more-wrapper]');
      if (wrapper) wrapper.style.display = 'none';

      const collectionHandle = grid.dataset?.collectionHandle || null;
      const need = (targetPage - ssrPage) * perPage;
      await fillCarryTo(need, collectionHandle);

      const batch = carry.splice(0, need);
      let lastValidPage = ssrPage;

      if (batch.length > 0) {
        const appended = appendUniqueProductCards(grid, batch);
        consumed += appended;
        lastValidPage = ssrPage + Math.floor(appended / perPage);
        currentPage = lastValidPage;

        if (ssrContainer) ssrContainer.dataset.ssrCurrentPage = String(currentPage);

        initProductCards();
      }

      // If the URL page was beyond available pages, reset it to the last real page —
      // but only once fillCarryTo has confirmed the catalog is actually exhausted
      // (noMoreResults). A transient fetch failure isn't proof page N doesn't exist;
      // rewriting the URL down in that case would strand the user on the wrong page
      // even though more content may still be there. Leave the URL alone and let the
      // now-visible manual button (via willShow below) retry from where we stopped.
      if (lastValidPage < targetPage && noMoreResults) setPageInUrl(lastValidPage);

      if (wrapper) {
        // noMoreResults (set by fillCarryTo from pagination.currentPage/totalPages, or an
        // empty response) is the ground truth — not whether the last fetched page was a
        // full perPage, which SearchSpring can trim short mid-catalog too.
        const willShow = carry.length > 0 || !noMoreResults;
        if (!willShow) hideLoadMore();
        else showManualLoadMore();
      }
    }

    async function doLoadMore() {
      if (loading) return;
      loading = true;
      loadMoreBtn.textContent = 'Loading...';
      loadMoreBtn.disabled = true;

      const gen = loadMoreGen;
      const collectionHandle = grid.dataset?.collectionHandle || null;
      await fillCarryTo(perPage, collectionHandle);

      // A sort/filter change (ssr:grid-replaced) bumps loadMoreGen — bail out so we
      // don't append stale results to a freshly-rendered grid.
      if (gen !== loadMoreGen) { loading = false; return; }

      const batch = carry.splice(0, perPage);

      if (batch.length > 0) {
        const appended = appendUniqueProductCards(grid, batch);
        consumed += appended;
        currentPage += 1;
        initProductCards();

        if (ssrContainer) ssrContainer.dataset.ssrCurrentPage = String(currentPage);
        setPageInUrl(currentPage);

        // noMoreResults (set by fillCarryTo from pagination.currentPage/totalPages, or an
        // empty response) is the ground truth — not whether the last fetched page was a
        // full perPage, which SearchSpring can trim short mid-catalog too.
        const willShow = carry.length > 0 || !noMoreResults;
        if (willShow) showManualLoadMore();
        else hideLoadMore();
      } else if (fetchFailed) {
        // Nothing new to show, but only because the request errored/timed out — we
        // don't actually know whether more pages exist. Restore the button instead of
        // hiding it so the user can retry rather than hitting a silent dead end.
        showManualLoadMore();
      } else {
        hideLoadMore();
      }

      loading = false;
    }

    loadMoreBtn.addEventListener('click', doLoadMore, { signal });

    let autoObserver = null;
    signal.addEventListener('abort', () => autoObserver?.disconnect());
    const armAutoObserver = () => {
      if (!autoTrigger || signal.aborted) return;
      autoObserver?.disconnect();
      autoObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !loading) {
          autoObserver.disconnect();
          autoObserver = null;
          doLoadMore();
        }
      }, { rootMargin: '200px' });
      autoObserver.observe(autoTrigger);
    };

    // Reset the control to auto-load mode (after a sort/filter renders a fresh page 1).
    function resetToAutoLoad() {
      loadMoreBtn.textContent = viewMoreLabel;
      loadMoreBtn.disabled = false;
      loadMoreBtn.classList.add('hidden');
      if (autoTrigger) autoTrigger.classList.remove('hidden');
      armAutoObserver();
    }

    if (autoTrigger && ssrPage === 1 && targetPage === 1) {
      armAutoObserver();
    } else if (autoTrigger) {
      autoTrigger.classList.add('hidden');
      loadMoreBtn.classList.remove('hidden');
    }

    // After a sort/filter SS-only render, the grid is replaced with page 1 of new
    // results — reset paging state and re-arm the auto-load observer.
    document.addEventListener('ssr:grid-replaced', (e) => {
      const data = e.detail?.data;
      loadMoreGen++;
      currentPage = 1;
      loading = false;
      carry = [];
      noMoreResults = false;
      if (ssrContainer) ssrContainer.dataset.ssrCurrentPage = '1';

      const wrapper = loadMoreBtn.closest('[data-ssr-load-more-wrapper]');

      // No data → snapshot restore of the original SSR page 1 (same padded
      // count/banner it had originally) — reset the buffer back to its
      // initial state rather than the sort/filter baseline below.
      if (!data) {
        consumed = shownCount;
        nextPageToFetch = Math.floor(shownCount / perPage) + 1;
        if (wrapper) resetToAutoLoad();
        return;
      }

      // A sort/filter change replaced the grid with a fresh SS-only page 1 —
      // no banner padding applies here, so the buffer's baseline is simply
      // however many products that fetch actually returned.
      consumed = data.results?.length || 0;
      nextPageToFetch = 2;

      if (!wrapper) return;

      // Trust the page/total-page count SS reports for this response — same reasoning
      // as fillCarryTo's isLastPage check. A "full page" test alone is unreliable:
      // confirmed live, SearchSpring can trim a page a couple of items short of perPage
      // (merchandising/exclusion rules) while it's nowhere near the last page. Only fall
      // back to the page-size test when pagination metadata is entirely missing.
      const pg = data.pagination;
      let hasMore;
      if (pg?.totalPages != null && pg?.currentPage != null) {
        hasMore = pg.currentPage < pg.totalPages;
      } else if (pg?.totalResults != null && pg?.end != null) {
        hasMore = pg.end < pg.totalResults;
      } else {
        const apiPerPage = pg?.perPage || perPage;
        hasMore = (data.results?.length || 0) >= apiPerPage;
      }
      if (!hasMore) {
        wrapper.style.display = 'none';
        autoObserver?.disconnect();
        autoObserver = null;
        return;
      }
      wrapper.style.display = '';
      resetToAutoLoad();
    }, { signal });

    if (targetPage > ssrPage) {
      catchUpPages();
    }
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

function init(gridSelector = '.collection-grid') {
  if (initialized) return;
  initialized = true;
  const grid = document.querySelector(gridSelector);
  if (!grid) return;
  if (new URLSearchParams(window.location.search).get('q')) { initSearchPage(); return; }

  // SSR mode: grid already rendered server-side from metafield data.
  // Skip initial fetch/reorder but wire up filter/sort interactions so
  // user actions trigger client-side SS rendering (ss-only mode).
  const isSSR = !!document.querySelector('[data-ssr-collection]');

  S.readCollectionFiltersFromURL();

  if (isSSR) {
    S.setRenderMode('ss-only');
    ssrMode = true;

    // Save SSR grid snapshot; filter snapshot taken after optional API facet bootstrap.
    ssrGridSnapshot = grid.innerHTML;
    const countEl = document.querySelector('[data-ss-result-count]');
    if (countEl) ssrCountSnapshot = countEl.textContent;

    const finishSSRInit = () => {
      const fc = document.getElementById('collection-filters-section');
      if (fc) ssrFiltersSnapshot = fc.innerHTML;
      hydrateSSRFilters(grid);

      window.addEventListener('popstate', () => { S.readCollectionFiltersFromURL(); syncAndApply({ replaceState: true }); });

      // If URL has active filters or sort, the SSR grid shows unfiltered data —
      // show loading state immediately and trigger client-side render.
      const hasActiveFilters = Object.keys(S.activeFilters).length > 0;
      const hasActiveSort = !!S.activeSort.field;
      if (hasActiveFilters || hasActiveSort) {
        grid.classList.add('searchspring-loading');
        doSSOnlyRender();
      } else {
        grid.classList.remove('searchspring-loading');
        // Personalization: on page 1 default sort with no filters, background re-fetch
        // with user tracking IDs (userId/sessionId/pageLoadId) and silently reorder.
        const urlPage = new URLSearchParams(window.location.search).get('page');
        if (!urlPage || urlPage === '1') {
          personalizeSSRGrid(grid);
        }
      }
    };

    ensureSSRFacetMarkupFromApi(grid).then(finishSSRInit).catch((err) => {
      console.error('[searchspring-collection] SSR facet bootstrap failed:', err);
      finishSSRInit();
    });
    return;
  }

  // The SS-SSR-enabled skeleton (cache miss) sets data-ssr-per-page on the grid —
  // the classic ss_active-without-SSR render (real S&D products, hybrid-eligible)
  // never does. No #CollectionFiltersForm-desktop is rendered for the skeleton
  // either, so fieldMap/currentFacets stay empty and determineRenderMode() would
  // misread that as "nothing to map" (vacuously true) rather than "can't map" —
  // wrongly picking hybrid the moment a sort with a Shopify-native equivalent is
  // chosen. Force ss-only here the same way the cache-hit branch above does — and,
  // like that branch, set S.renderMode synchronously too (not just ssrMode).
  // collection.js's popstate listener runs independently of this module and gates
  // its own morph on isSSOnlyMode() (S.renderMode), which otherwise stays at its
  // 'hybrid' default until the fetch below resolves — or forever, if it fails.
  if (grid.hasAttribute('data-ssr-per-page')) {
    ssrMode = true;
    S.setRenderMode('ss-only');
  }

  const sdForm = document.getElementById('CollectionFiltersForm-desktop');
  if (sdForm) { const r = buildFieldMap(sdForm); S.setFieldMap(r.fieldMap); S.setSdFilterDefs(r.sdFilterDefs); }
  grid.classList.add('searchspring-loading');

  fetchSearchspringProducts(grid.dataset?.collectionHandle || null, 800).then(async (data) => {
    if (!data) { grid.classList.remove('searchspring-loading'); return; }
    S.setCurrentFacets(data.facets);
    S.setSdOnlyFacets(getSDOnlyFilterDefs(S.sdFilterDefs, data.facets));
    // Same ssrMode-first gate as syncAndApply() — determineRenderMode() would otherwise
    // read the still-empty fieldMap/currentFacets as "nothing to map" (vacuously true)
    // and misreturn 'hybrid' for an empty-filters/default-sort request, undoing the
    // ss-only force from the top of init() before renderedSSOnly's own recovery below
    // even runs (that recovery only fires when data.results is non-empty, so a genuinely
    // empty SS response would otherwise strand renderMode at 'hybrid' while ssrMode
    // stays true).
    if (ssrMode) {
      S.setRenderMode('ss-only');
    } else {
      S.setRenderMode(S.determineRenderMode());
    }
    const fc = document.getElementById('collection-filters-section');
    if (fc && data.facets) renderFacets(data.facets, fc, facetOpts());
    let renderedSSOnly = false;
    if (S.renderMode === 'ss-only') {
      S.setRenderMode('ss-only'); renderSSOnlyGrid(data, grid, { facetSelectionOrder: S.facetSelectionOrder }); renderedSSOnly = true;
    } else {
      const cards = grid.querySelectorAll(':scope > [data-product-handle]');
      if (cards.length === 0 && data.results?.length > 0) { S.setRenderMode('ss-only'); renderSSOnlyGrid(data, grid, { facetSelectionOrder: S.facetSelectionOrder }); renderedSSOnly = true; }
      else if (cards.length > 0 && data.results?.length > 0) {
        const handles = new Set(data.results.map(r => r.handle));
        if (Array.from(cards).some(c => handles.has(c.dataset.productHandle))) reorderProductCards(data, grid);
        else { S.setRenderMode('ss-only'); renderSSOnlyGrid(data, grid, { facetSelectionOrder: S.facetSelectionOrder }); renderedSSOnly = true; }
      } else reorderProductCards(data, grid);
    }

    // Wires the "View More Products" button/auto-trigger when a [data-ssr-load-more]
    // wrapper is present (SS-only skeleton render on an SSR cache miss) — no-ops if the
    // markup isn't there (classic ss_active render shows every product via Liquid).
    // Must run AFTER page 1 is in the DOM: if a deep-linked ?page=N is caught up here,
    // hydrateSSRFilters kicks off its own catchUpPages() fetch that appends starting
    // from page 2 — calling this before the fetch above resolves would let that catch-up
    // race renderSSOnlyGrid's innerHTML wipe for page 1, silently dropping whichever
    // finishes first.
    if (renderedSSOnly) {
      hydrateSSRFilters(grid);
      // hydrateSSRFilters already owns pagination visibility for a deep-linked page via
      // catchUpPages(); dispatching ssr:grid-replaced too would reset its in-flight
      // carry/currentPage mid-fetch. Only notify for the plain page-1 case, where
      // hydrateSSRFilters has no data of its own to decide hasMore from.
      const urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
      if (urlPage <= 1) {
        document.dispatchEvent(new CustomEvent('ssr:grid-replaced', { detail: { data } }));
      }
    }

    setupTracking(data, grid, { module: 'category' });
    await waitForAboveFoldImagesLoaded(grid);
    grid.classList.remove('searchspring-loading');
  });
  window.addEventListener('popstate', () => { S.readCollectionFiltersFromURL(); syncAndApply({ replaceState: true }); });
}

export const provider = {
  init,
  fetchProducts: () => fetchSearchspringProducts(),
  reorderGrid: reorderProductCards,
  onRendered: (data, el) => { setupTracking(data, el, { module: 'category' }); rerenderFacetsAfterMorph(data); },
  isSSOnlyMode: () => S.renderMode === 'ss-only',
  renderFallback: (data, el) => {
    S.setRenderMode('ss-only');
    renderSSOnlyGrid(data, el, { facetSelectionOrder: S.facetSelectionOrder });
    setupTracking(data, el, { module: 'category' });
    const fc = document.getElementById('collection-filters-section');
    if (fc && data.facets) { S.setCurrentFacets(data.facets); renderFacets(data.facets, fc, { activeFilters: S.activeFilters, activeSort: S.activeSort, onFacetChange: handleFacetChange, onSortChange: handleSortChange }); }
  },
};
